import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { useChatStreaming } from '@/hooks/use-chat-streaming'
import { useIsMobile } from '@/hooks/use-is-mobile'
import { documentService } from '@/services/document.service'
import { externalJobsService } from '@/services/external-jobs.service'
import { useChatStore } from '@/stores/chat-store'
import type { Document, DocumentDetail } from '@/types'
import { Clock, Download, Eye, History, Loader, Sparkles, Target, Trash2 } from 'lucide-react'
import { useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { OverallScore, ScoreBreakdown } from './score-breakdown'

function getAtsTailorJobTitle(promptSummary?: string): string | null {
  if (!promptSummary) return null
  const trimmed = promptSummary.trim()
  const prefixes = ['Tailor this resume for ', 'ATS JD tailor: ']
  for (const prefix of prefixes) {
    if (!trimmed.startsWith(prefix)) continue
    const after = trimmed.slice(prefix.length).trim()
    const title = after.split(' — ')[0]?.trim() || ''
    if (title) return title
  }
  return null
}

function safeFilenamePart(value: string): string {
  return value
    .replace(/[<>:"/\\|?*]/g, '_')
    .split('')
    .filter((ch) => ch.charCodeAt(0) >= 32)
    .join('')
    .replace(/\s+/g, ' ')
    .trim()
}

interface ResumeScoreCardProps {
  doc: Document
  detail?: DocumentDetail | null
  onDelete: (doc: Document) => void
  onDownload: (doc: Document) => void
  isDeleting: boolean
  processingProgress?: number | null
  processingStatusText?: string | null
  isPrimary?: boolean
}

function formatTimeAgo(dateString: string): string {
  const date = new Date(dateString)
  const now = new Date()
  const diffMs = now.getTime() - date.getTime()
  const diffHours = Math.floor(diffMs / (1000 * 60 * 60))
  const diffDays = Math.floor(diffHours / 24)
  const diffMonths = Math.floor(diffDays / 30)
  const diffYears = Math.floor(diffDays / 365)

  if (diffHours < 1) return 'just now'
  if (diffHours < 24) return `${diffHours} hours ago`
  if (diffDays < 30) return `${diffDays} days ago`
  if (diffMonths < 12) return `${diffMonths} months ago`
  return `${diffYears} years ago`
}

export function ResumeScoreCard({
  doc,
  detail,
  onDelete,
  onDownload,
  isDeleting,
  processingProgress,
  processingStatusText,
  isPrimary,
}: ResumeScoreCardProps) {
  const isMobile = useIsMobile()
  const navigate = useNavigate()
  const { sendMessageStreaming } = useChatStreaming()
  const {
    chatId,
    setUserDocumentId,
    setPreviewPanelOpen,
    setResumeContent,
    setOriginalResumeContent,
  } = useChatStore()
  const [showHistory, setShowHistory] = useState(false)
  const [isMatchingJobs, setIsMatchingJobs] = useState(false)

  // Extract resume title and scores from detail if available
  const resumeName = doc.original_filename.replace(/\.[^.]+$/, '')
  const parsedData = detail?.parsed_resume_data
  const targetRoleRaw = parsedData?.summary
    ?.match(
      /(?:seeking|target(?:ing)?|looking for)\s+(?:a\s+)?(.{2,40}?)(?:\s+(?:to|in|at|and|with|where|that)\b|[,.]|$)/i,
    )?.[1]
    ?.trim()
  const targetRole = targetRoleRaw && targetRoleRaw.length >= 4 ? targetRoleRaw : null

  // Use match scores if available, otherwise show placeholder
  const qualityScore = detail?.quality_score
  const hasScore = qualityScore != null

  const handleOpenPreview = async () => {
    if (!isReady) return

    setUserDocumentId(doc.id)
    setPreviewPanelOpen(true)

    try {
      const resolvedDetail = detail ?? (await documentService.getDocument(doc.id))
      if (resolvedDetail.parsed_resume_data) {
        setOriginalResumeContent(resolvedDetail.parsed_resume_data)
        if (!resolvedDetail.draft_content_json) {
          setResumeContent(resolvedDetail.parsed_resume_data)
        }
      }
      if (resolvedDetail.draft_content_json) {
        setResumeContent(resolvedDetail.draft_content_json)
      }
    } catch (error) {
      console.error('Failed to load resume preview content:', error)
    }
  }

  const handleImproveWithCoach = () => {
    if (!chatId) return
    const improvePrompt = `I'd like to improve my resume "${doc.original_filename}". Please analyze it and suggest improvements.`
    if (isMobile) {
      sessionStorage.setItem(
        'pending_coach_message',
        JSON.stringify({ message: improvePrompt, documentId: doc.id }),
      )
      navigate('/chat')
      return
    }
    sendMessageStreaming(improvePrompt, [doc.id])
  }

  const handleMatchWithJobs = async () => {
    setIsMatchingJobs(true)
    try {
      const numericChatId = chatId ? Number(chatId) : null
      await externalJobsService.triggerMatchResume(doc.id, numericChatId)
    } catch (err) {
      console.error('Failed to trigger job matching:', err)
    } finally {
      setIsMatchingJobs(false)
    }
  }

  const isReady = doc.status === 'ready'
  const isProcessing = doc.status === 'processing' || doc.status === 'uploading'

  const matchedJobsHref = `/opportunities?resume_id=${doc.id}&tab=all&sort=score_desc#matched-resume-${doc.id}`
  const isJobMatchingInProgress = isMatchingJobs || doc.job_matching_status === 'matching'
  const hasCompletedJobMatching = doc.job_matching_status === 'completed'

  return (
    <Card className="rounded-2xl border bg-white dark:bg-gray-900/50 shadow-sm">
      <CardContent className="p-3 sm:p-6">
        {/* Header row */}
        <div className="flex items-start justify-between mb-4">
          <div>
            <div className="flex items-center gap-2 mb-0.5">
              <h3 className="text-lg font-semibold">{resumeName}</h3>
              {isPrimary && (
                <span className="inline-flex items-center text-xs font-medium text-gray-600 dark:text-gray-400 bg-gray-100 dark:bg-gray-800 px-2.5 py-0.5 rounded-full border border-gray-200 dark:border-gray-700">
                  Primary
                </span>
              )}
            </div>
            <p className="text-sm text-muted-foreground">
              Last updated: {formatTimeAgo(doc.updated_at || doc.created_at)}
            </p>
            {targetRole && (
              <span className="inline-flex items-center gap-1.5 text-xs text-muted-foreground bg-gray-100 dark:bg-gray-800 px-2.5 py-0.5 rounded-full mt-1 max-w-[240px] truncate">
                {targetRole}
              </span>
            )}
          </div>
          <div className="flex items-center gap-2 shrink-0">
            {isProcessing && (
              <span className="inline-flex items-center gap-1.5 text-sm text-blue-600 bg-blue-50 dark:bg-blue-950/30 px-3 py-1 rounded-full">
                <Loader className="h-3.5 w-3.5 animate-spin" />
                Processing
              </span>
            )}
            <button
              type="button"
              onClick={() => void handleOpenPreview()}
              disabled={!isReady}
              className="p-1.5 rounded-md hover:bg-blue-100 dark:hover:bg-blue-900/30 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              title="Preview resume"
              aria-label="Preview resume"
            >
              <Eye className="h-4 w-4 text-blue-600" />
            </button>
          </div>
        </div>

        {/* Processing progress bar */}
        {isProcessing && processingProgress != null && processingProgress > 0 && (
          <div className="mb-4">
            <div className="flex items-center justify-between mb-1">
              <span className="text-xs text-muted-foreground">
                {processingStatusText || 'Analyzing resume...'}
              </span>
              <span className="text-xs text-muted-foreground tabular-nums">
                {Math.round(processingProgress)}%
              </span>
            </div>
            <div className="h-1.5 bg-muted rounded-full overflow-hidden">
              <div
                className="h-full bg-blue-500 rounded-full transition-all duration-500"
                style={{ width: `${processingProgress}%` }}
              />
            </div>
          </div>
        )}

        {/* Score section */}
        {isReady && (
          <div className="flex gap-3 sm:gap-4 md:gap-8 mb-6">
            <OverallScore score={hasScore ? qualityScore.overall : 0} className="shrink-0" />
            <div className="flex-1 min-w-0 space-y-2.5">
              <ScoreBreakdown
                label="Skills coverage"
                value={hasScore ? qualityScore.skills_coverage : 0}
              />
              <ScoreBreakdown
                label="Experience clarity"
                value={hasScore ? qualityScore.experience_clarity : 0}
              />
              <ScoreBreakdown
                label="Formatting & readability"
                value={hasScore ? qualityScore.formatting : 0}
              />
            </div>
          </div>
        )}

        {!isReady && !isProcessing && doc.status === 'invalid_document' && (
          <div className="mb-6 p-3 rounded-lg bg-yellow-50 dark:bg-yellow-950/20 text-sm text-yellow-700 dark:text-yellow-400">
            This is not a valid resume. Please upload a valid one and try again.
          </div>
        )}

        {!isReady && !isProcessing && doc.status === 'failed' && (
          <div className="mb-6 p-3 rounded-lg bg-red-50 dark:bg-red-950/20 text-sm text-red-600">
            Processing failed. Please try uploading again.
          </div>
        )}

        {/* Action buttons */}
        <div className="flex flex-wrap gap-2">
          <Button
            size="sm"
            className="gap-2 bg-blue-600 text-white hover:bg-blue-700"
            onClick={handleImproveWithCoach}
            disabled={!isReady || !chatId}
          >
            <Sparkles className="h-4 w-4" />
            Improve with Coach
          </Button>
          {hasCompletedJobMatching ? (
            <Button
              size="sm"
              className="gap-2 bg-blue-600 text-white hover:bg-blue-700 no-underline"
              asChild
            >
              <Link to={matchedJobsHref}>
                <Target className="h-4 w-4" />
                Goto Resume Scan Agent
              </Link>
            </Button>
          ) : (
            <Button
              variant="outline"
              size="sm"
              className="gap-2"
              onClick={handleMatchWithJobs}
              disabled={!isReady || isJobMatchingInProgress}
            >
              {isJobMatchingInProgress ? (
                <Loader className="h-4 w-4 animate-spin" />
              ) : (
                <Target className="h-4 w-4" />
              )}
              {isJobMatchingInProgress ? 'Generating Matches...' : 'Match with Jobs'}
            </Button>
          )}
          <Button
            variant="outline"
            size="sm"
            className="gap-2"
            onClick={() => onDownload(doc)}
            disabled={!isReady}
          >
            <Download className="h-4 w-4" />
            Download
          </Button>
          {detail?.revisions && detail.revisions.length > 0 && (
            <Button
              variant="outline"
              size="sm"
              className="gap-2"
              onClick={() => setShowHistory(!showHistory)}
              disabled={!isReady}
            >
              <History className="h-4 w-4" />
              History
            </Button>
          )}
          <Button
            variant="outline"
            size="sm"
            className="gap-2 text-destructive hover:text-destructive hover:bg-destructive/10"
            onClick={() => onDelete(doc)}
            disabled={isDeleting || isProcessing}
          >
            <Trash2 className="h-4 w-4" />
          </Button>
        </div>
        {/* Revision history */}
        {showHistory && detail?.revisions && detail.revisions.length > 0 && (
          <div className="mt-4 pt-4 border-t">
            <h4 className="text-sm font-medium mb-2 flex items-center gap-2">
              <Clock className="h-4 w-4" />
              Revision History
            </h4>
            <div className="space-y-2">
              {detail.revisions
                .sort((a, b) => b.revision_number - a.revision_number)
                .map((rev) => (
                  <div
                    key={rev.id}
                    className="flex items-center justify-between text-sm py-1.5 px-2 rounded hover:bg-muted/50"
                  >
                    <span>
                      {(() => {
                        const atsJobTitle = getAtsTailorJobTitle(rev.prompt_summary)
                        if (!atsJobTitle) return `Version ${rev.revision_number} - ${rev.status}`
                        const resumeBase = safeFilenamePart(
                          (doc.original_filename || 'resume').replace(/\.[^/.]+$/, ''),
                        )
                        const jobBase = safeFilenamePart(atsJobTitle)
                        return `${resumeBase}_${jobBase} - ${rev.status}`
                      })()}
                    </span>
                    {rev.status === 'ready' && (
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-7"
                        onClick={async () => {
                          try {
                            const atsJobTitle = getAtsTailorJobTitle(rev.prompt_summary)
                            const preferredFilename = atsJobTitle
                              ? `${safeFilenamePart((doc.original_filename || 'resume').replace(/\.[^/.]+$/, ''))}_${safeFilenamePart(atsJobTitle)}.pdf`
                              : undefined
                            const result = await documentService.getRevisionDownloadUrl(
                              doc.id,
                              rev.id,
                              'pdf',
                              preferredFilename,
                            )
                            window.open(result.download_url, '_blank')
                          } catch (err) {
                            console.error('Download failed:', err)
                          }
                        }}
                      >
                        <Download className="h-3.5 w-3.5" />
                      </Button>
                    )}
                  </div>
                ))}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  )
}
